/**
 * Shared helpers for rendering venue activity events — used by the Activity
 * page feed and the per-booking History section in the booking modals.
 */
import {
  Activity, CalendarPlus, CalendarX2, CalendarClock, QrCode, Ban,
  CircleSlash, Building2, UserCog, Trash2,
} from 'lucide-react';

export interface ActivityEntry {
  id: number;
  action_type: string;
  description: string;
  user: { id: number; name: string } | null;
  target_type: string | null;
  target_id: number | null;
  before_data: Record<string, unknown> | null;
  after_data: Record<string, unknown> | null;
  created_at: string | null;
}

export const ACTION_TYPES: Record<string, string> = {
  'booking.created': 'Booking logged',
  'booking.rescheduled': 'Booking rescheduled',
  'booking.cancelled': 'Booking cancelled',
  'booking.deleted': 'Booking deleted',
  'checkin.completed': 'Check-in',
  'slot.blocked': 'Slot blocked',
  'slot.unblocked': 'Slot unblocked',
  'blackout.added': 'Closure added',
  'blackout.removed': 'Closure removed',
  'venue.updated': 'Venue updated',
  'staff.added': 'Staff added',
  'staff.updated': 'Staff access changed',
  'staff.deactivated': 'Staff deactivated',
  'staff.reactivated': 'Staff reactivated',
  'staff.password_reset': 'Staff password reset',
};

export const actionIcon = (type: string) => {
  if (type === 'booking.created') return CalendarPlus;
  if (type === 'booking.rescheduled') return CalendarClock;
  if (type === 'booking.cancelled') return CalendarX2;
  if (type === 'booking.deleted') return Trash2;
  if (type === 'checkin.completed') return QrCode;
  if (type === 'slot.blocked' || type === 'blackout.added') return Ban;
  if (type === 'slot.unblocked' || type === 'blackout.removed') return CircleSlash;
  if (type === 'venue.updated') return Building2;
  if (type.startsWith('staff.')) return UserCog;
  return Activity;
};

export const actionChipClass = (type: string) => {
  if (type.startsWith('booking.cancelled') || type === 'booking.deleted') return 'bg-red-50 text-red-700';
  if (type === 'booking.created') return 'bg-green-50 text-green-700';
  if (type === 'checkin.completed') return 'bg-indigo-50 text-indigo-700';
  if (type.startsWith('staff.')) return 'bg-purple-50 text-purple-700';
  if (type.startsWith('slot.') || type.startsWith('blackout.')) return 'bg-amber-50 text-amber-700';
  return 'bg-gray-100 text-gray-700';
};

export const formatWhen = (iso: string | null): string => {
  if (!iso) return '';
  const date = new Date(iso);
  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();
  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (sameDay) return `Today ${time}`;
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return `Yesterday ${time}`;
  return `${date.toLocaleDateString([], { day: 'numeric', month: 'short' })} ${time}`;
};
